import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { usePerspective } from "../../contexts/app/provider";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import {
  parseParquetData,
  parseParquetDataWithRefresh,
  type ParsedParquetData,
  isChunkedParsedData,
} from "../../utils/parquet";
import dayjs from "../../utils/dayjs";
import {
  GetTransformedParquetDownloadUrlDocument,
  GetTransformedRawParquetDocument,
} from "../../services/perspective_api/__generated__/perspective_server-types";
import { getSupplyWalkEditTableData } from "../../utils/mui_flat_table_data";
import { FlatTable } from "./FlatTable";
import { getWOSMeasureKey } from "../../utils/supply_color_coding";

export interface SupplyWalkEditableNestedDetailsProps {
  cacheRef: React.MutableRefObject<
    Record<
      string,
      {
        loading: boolean;
        error: string | null;
        parsed: ParsedParquetData | null;
      }
    >
  >;
  cacheKey: string;
  sku: string;
  location: string;
  supplier: string;
  parentEdit?: {
    quantity: number;
    total_cost?: number;
    unit_cost?: number;
    orderPlacedDate: string;
    deliveryDate: string;
    approved?: boolean;
  } | null;
  poId?: string;
  edits: Map<
    string,
    {
      committedValues: Record<string, string>;
      originalValues: Record<string, string>;
    }
  >;
  getEditForRow: (
    poId: string,
    sku: string,
    location: string
  ) =>
    | {
        committedValues: Record<string, string>;
        originalValues: Record<string, string>;
      }
    | undefined;
}

// Top-level detail component (decoupled from container re-renders)
export const SupplyWalkEditableNestedDetails: React.FC<
  SupplyWalkEditableNestedDetailsProps
> = ({
  cacheRef,
  cacheKey,
  sku,
  location,
  supplier,
  parentEdit: initialParentEdit,
  poId,
}) => {
  const supplyGuard = React.useContext(SupplyWalkEditsContext);
  const theme = useTheme();
  const { perspectiveApiService } = usePerspective();
  const { mainRun, lastSuccessfulRun, comparedRun } =
    React.useContext(VersionedDataContext);

  // Helper function to derive parentEdit from storage
  const deriveParentEdit = React.useCallback(() => {
    // Get edit from persistent storage via context
    if (poId && sku && location && supplyGuard?.getEdit) {
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";
      const rowKey = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
      const stored = supplyGuard.getEdit(rowKey);
      if (stored) {
        const committed = stored.committedValues;
        const q = committed?.quantity ?? committed?.["quantity"];
        const tc = committed?.total_cost ?? committed?.["total_cost"];
        const uc = committed?.unit_cost ?? committed?.["unit_cost"];
        const opd =
          committed?.order_placed_date ?? committed?.["order_placed_date"];
        const dd =
          committed?.order_delivery_date ?? committed?.["order_delivery_date"];

        // Merge committed values with initialParentEdit to ensure we have all required fields
        // This handles cases where only some fields were edited (e.g., only quantity)
        if (initialParentEdit) {
          return {
            quantity:
              q != null && q !== ""
                ? Number(String(q).replace(/,/g, ""))
                : initialParentEdit.quantity,
            total_cost:
              tc != null && tc !== ""
                ? Number(String(tc).replace(/,/g, ""))
                : initialParentEdit.total_cost,
            unit_cost:
              uc != null && uc !== ""
                ? Number(String(uc).replace(/,/g, ""))
                : initialParentEdit.unit_cost,
            orderPlacedDate:
              opd != null && opd !== ""
                ? String(opd)
                : initialParentEdit.orderPlacedDate,
            deliveryDate:
              dd != null && dd !== ""
                ? String(dd)
                : initialParentEdit.deliveryDate,
            approved: stored.approved ?? false,
          };
        }

        // If no initialParentEdit but we have committed values, use them if we have required fields
        // We need quantity and order_placed_date for recalculation
        if (q != null && opd) {
          return {
            quantity: Number(String(q).replace(/,/g, "")),
            total_cost:
              tc != null ? Number(String(tc).replace(/,/g, "")) : undefined,
            unit_cost:
              uc != null ? Number(String(uc).replace(/,/g, "")) : undefined,
            orderPlacedDate: String(opd),
            deliveryDate: dd != null ? String(dd) : "",
            approved: stored.approved ?? false,
          };
        }
      }
    }

    // Fall back to initialParentEdit
    if (initialParentEdit) {
      return initialParentEdit;
    }

    return null;
  }, [
    initialParentEdit,
    supplyGuard,
    poId,
    sku,
    location,
    supplier,
    mainRun,
    lastSuccessfulRun,
  ]);

  // Store parentEdit in state so changes trigger re-render
  const [parentEdit, setParentEdit] = useState(() => deriveParentEdit());
  const [entry, setEntry] = useState<{
    loading: boolean;
    error: string | null;
    parsed: ParsedParquetData | null;
  }>(
    () =>
      cacheRef.current[cacheKey] || {
        loading: false,
        error: null,
        parsed: null,
      }
  );

  // Sync parentEdit with latest value from storage when component mounts or dependencies change
  // This ensures edits made while the row was collapsed are picked up when it's expanded
  useEffect(() => {
    // Force update on mount to ensure we have the latest values from storage
    // This is critical for recalculating when the row is expanded after edits were made while collapsed
    setParentEdit((prev) => {
      // On mount, prev might be the initial value from useState, so always check storage
      const latest = deriveParentEdit();

      // If this is the initial mount (prev matches initial), always use latest from storage
      const isInitialMount =
        prev === null ||
        (initialParentEdit &&
          prev.quantity === initialParentEdit.quantity &&
          prev.orderPlacedDate === initialParentEdit.orderPlacedDate &&
          prev.deliveryDate === initialParentEdit.deliveryDate);

      if (isInitialMount) {
        // On mount, always use the latest from storage to ensure edits are reflected
        return latest;
      }

      // After mount, only update if the value actually changed
      if (
        prev?.quantity !== latest?.quantity ||
        prev?.total_cost !== latest?.total_cost ||
        prev?.unit_cost !== latest?.unit_cost ||
        prev?.orderPlacedDate !== latest?.orderPlacedDate ||
        prev?.deliveryDate !== latest?.deliveryDate ||
        prev?.approved !== latest?.approved
      ) {
        return latest;
      }
      return prev;
    });
  }, [deriveParentEdit, initialParentEdit]);

  // Force refresh when run changes
  useEffect(() => {
    const handleRunChange = () => {
      // Clear cache for this specific key to force refetch
      delete cacheRef.current[cacheKey];
      setEntry({
        loading: false,
        error: null,
        parsed: null,
      });
    };

    window.addEventListener("runChanged", handleRunChange);
    return () => {
      window.removeEventListener("runChanged", handleRunChange);
    };
  }, [cacheRef, cacheKey]);

  const now = dayjs();
  const minDate = now.startOf("year").format("YYYY-MM-DD");
  const maxDate = now.add(11, "month").endOf("month").format("YYYY-MM-DD");

  const viewParams = React.useMemo(
    () => ({
      segments: ["sku", "location"],
      dateHierarchy: "month",
    }),
    []
  );

  // Listen for commits, undo, approval toggle, and supply edits changed events for this sku/location/poId and trigger recalculation
  useEffect(() => {
    const commitHandler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as
          | { rowKey?: string }
          | undefined;
        const runAttemptId =
          mainRun?.runSettings?.runAttemptId ||
          lastSuccessfulRun?.runSettings?.runAttemptId ||
          "no-run";
        const keySuffix = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
        if (!detail?.rowKey || !detail.rowKey.endsWith(keySuffix)) {
          return;
        }

        const newParentEdit = deriveParentEdit();
        setParentEdit(newParentEdit);
      } catch (_) {}
    };

    const undoHandler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as
          | { rowKey?: string }
          | undefined;
        const runAttemptId =
          mainRun?.runSettings?.runAttemptId ||
          lastSuccessfulRun?.runSettings?.runAttemptId ||
          "no-run";
        const keySuffix = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
        if (!detail?.rowKey || !detail.rowKey.endsWith(keySuffix)) {
          return;
        }

        // Update parentEdit to null/original to show original values
        setParentEdit(deriveParentEdit());
      } catch (_) {}
    };

    const approvalToggleHandler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as
          | { rowKey?: string; checked?: boolean }
          | undefined;
        const runAttemptId =
          mainRun?.runSettings?.runAttemptId ||
          lastSuccessfulRun?.runSettings?.runAttemptId ||
          "no-run";
        const keySuffix = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
        if (!detail?.rowKey || !detail.rowKey.endsWith(keySuffix)) {
          return;
        }

        // When approval is toggled, refresh parentEdit to reflect current state
        // This ensures edits are visible in details view after unapproving
        setParentEdit(deriveParentEdit());
      } catch (_) {}
    };

    // Also listen to supplyEditsChanged to catch any edit storage updates
    // This ensures we catch edits made after unapproving
    const supplyEditsChangedHandler = () => {
      // Only refresh if there's actually an edit for this row
      // This avoids unnecessary re-renders for other rows' edits
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";
      const rowKey = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
      const stored = supplyGuard?.getEdit?.(rowKey);
      // Refresh if there's a stored edit (with committed values OR just approval flag)
      if (stored) {
        // Use a small delay to ensure storage is fully updated
        setTimeout(() => {
          const newParentEdit = deriveParentEdit();
          setParentEdit(newParentEdit);
        }, 0);
      }
    };

    window.addEventListener("poEditCommitted", commitHandler as EventListener);
    window.addEventListener("poEditUndone", undoHandler as EventListener);
    window.addEventListener(
      "poApprovalToggled",
      approvalToggleHandler as EventListener
    );
    window.addEventListener(
      "supplyEditsChanged",
      supplyEditsChangedHandler as EventListener
    );
    return () => {
      window.removeEventListener(
        "poEditCommitted",
        commitHandler as EventListener
      );
      window.removeEventListener("poEditUndone", undoHandler as EventListener);
      window.removeEventListener(
        "poApprovalToggled",
        approvalToggleHandler as EventListener
      );
      window.removeEventListener(
        "supplyEditsChanged",
        supplyEditsChangedHandler as EventListener
      );
    };
  }, [
    poId,
    sku,
    location,
    supplier,
    mainRun,
    lastSuccessfulRun,
    deriveParentEdit,
    supplyGuard,
  ]);

  useEffect(() => {
    // Build filters conservatively to avoid schema errors (omit supplier if not available)
    const filters: [string, string, string[]][] = [
      ["sku", "in", [sku]],
      ["location", "in", [location]],
    ];

    const variables = {
      datasetName: "supply_walk_test",
      transformId: "supply_walk_editable_detail",
      folder: "outputs",
      runSettingsJson: (mainRun || lastSuccessfulRun)?.runSettings || null,
      comparedToRunSettingsJson: comparedRun?.runSettings || null,
      format: "parquet" as const,
      params: {
        min_date: minDate,
        max_date: maxDate,
        date_field: "month",
        segmentation_fields: ["sku", "location"],
        filters,
        post_filters: [],
      },
    };

    // Use simple cache key - no need to include parentEdit since we calculate on-the-fly
    const cached = cacheRef.current[cacheKey];
    if (cached && (cached.parsed || cached.loading)) {
      setEntry(cached);
      return;
    }
    const loadingEntry = { loading: true, error: null, parsed: null };
    cacheRef.current[cacheKey] = loadingEntry;
    setEntry(loadingEntry);

    // Failure is latched here and resolved on `complete` — see the handlers.
    let pendingError: string | null = null;
    let sawData = false;

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({ query: GetTransformedRawParquetDocument, variables })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          const parquetData = payload?.parquetData;
          // Latch a failure rather than rendering it straight away. The
          // server's FIRST emit reports cached state, which can be a previous
          // attempt's failure that the fresh run then supersedes — rendering
          // it immediately flashes an error a moment before the data lands.
          // `complete` decides, once we know nothing better is coming.
          if (payload?.status === "FAILED" || payload?.errorMessage) {
            pendingError = String(
              payload?.errorMessage || "Transformation failed"
            );
            return;
          }
          // Still in flight (LOADING, or a progress message with no payload).
          if (!parquetData || typeof parquetData !== "string") return;
          // Set before awaiting: `complete` can fire while the parse is still
          // in flight, and must not mistake that for "no data arrived".
          sawData = true;
          pendingError = null;
          const isBase64 = !parquetData.startsWith("http");
          const refreshVariables = {
            datasetName: variables.datasetName,
            transformId: variables.transformId,
            folder: variables.folder,
            runSettingsJson: variables.runSettingsJson,
            comparedToRunSettingsJson: variables.comparedToRunSettingsJson,
            params: variables.params,
          };
          try {
            const parsed = isBase64
              ? await parseParquetData(parquetData, true)
              : await parseParquetDataWithRefresh(parquetData, async () => {
                  const result = await client.query({
                    query: GetTransformedParquetDownloadUrlDocument,
                    variables: refreshVariables,
                  });
                  return result.data?.getTransformedParquetDownloadUrl?.downloadUrl ?? null;
                });
            const successEntry = { loading: false, error: null, parsed };
            cacheRef.current[cacheKey] = successEntry;
            setEntry(successEntry);
          } catch (e: any) {
            const errorEntry = {
              loading: false,
              error: String(e?.message || e),
              parsed: null,
            };
            cacheRef.current[cacheKey] = errorEntry;
            setEntry(errorEntry);
          }
        },
        error: (err: any) => {
          const errorEntry = {
            loading: false,
            error: String(err?.message || err),
            parsed: null,
          };
          cacheRef.current[cacheKey] = errorEntry;
          setEntry(errorEntry);
        },
        complete: () => {
          // The stream ended. If data arrived, the handler above already
          // rendered it. Otherwise surface the latched failure — or say the
          // request returned nothing, so a silent server-side failure can
          // never leave the panel on "Loading..." forever.
          if (sawData) return;
          const errorEntry = {
            loading: false,
            error: pendingError ?? "No data returned for this row",
            parsed: null,
          };
          cacheRef.current[cacheKey] = errorEntry;
          setEntry(errorEntry);
        },
      });

    return () => {
      sub.unsubscribe();
    };
    // Only re-run if cacheKey/filters change - NOT parentEdit
  }, [
    cacheRef,
    cacheKey,
    perspectiveApiService,
    sku,
    location,
    minDate,
    maxDate,
    mainRun,
    lastSuccessfulRun,
    comparedRun,
  ]);

  // Memoize child to ensure it's recreated when parentEdit changes (full table only; chunked not supported here)
  const child = React.useMemo(() => {
    if (!entry?.parsed || isChunkedParsedData(entry.parsed)) return null;

    const base = getSupplyWalkEditTableData({
      arrowTable: entry.parsed.arrowTable,
      columns: entry.parsed.columns,
      viewParams,
      theme,
      parentEdit,
    });

    if (!base) return base;

    // Frontend recalculation: apply edits to all calculated values
    // The transform provides both "approved" and "review" rows, so they'll always be found
    if (parentEdit) {
      const columnsIndexByKey: Record<string, number> = {};
      base.columns.forEach((c: any, idx: number) => {
        columnsIndexByKey[c.key] = idx;
      });

      const getRowOriginal = base.getRow;
      const wrappedGetRow = (index: number) => {
        const r = { ...getRowOriginal(index) };
        if (!r || typeof r !== "object") return r;

        try {
          const segmentationKey = r.segmentationKey;
          if (segmentationKey) {
            const findRowIndexByMeasure = (
              measureName: string
            ): number | null => {
              const target = measureName.toLowerCase();
              for (
                let i = Math.max(0, index - 20);
                i < Math.min(base.rowCount, index + 50);
                i++
              ) {
                const rr = getRowOriginal(i);
                if (
                  rr &&
                  rr.segmentationKey === segmentationKey &&
                  String(rr.measure || "").toLowerCase() === target
                ) {
                  return i;
                }
              }
              return null;
            };

            const idxStart = findRowIndexByMeasure("starting inventory");
            const idxArrivalsReview = findRowIndexByMeasure(
              "purchase order arrivals (review)"
            );
            const idxArrivalsApproved = findRowIndexByMeasure(
              "purchase order arrivals (approved)"
            );
            const idxArrivalsOpen = findRowIndexByMeasure(
              "purchase order arrivals (open)"
            );
            const idxArrivalsTotal = findRowIndexByMeasure(
              "purchase order arrivals (total)"
            );
            const idxPlaced = findRowIndexByMeasure("purchase order to place");
            const idxForecast = findRowIndexByMeasure("forecasted sales");
            const idxEnding = findRowIndexByMeasure("ending inventory");
            const idxUnfulfilled = findRowIndexByMeasure("unfulfilled demand");
            const idxInStockRate = findRowIndexByMeasure("in stock rate");
            const idxStartingMonths = findRowIndexByMeasure(
              getWOSMeasureKey(
                "starting",
                viewParams.dateHierarchy
              ).toLowerCase()
            );
            const idxEndingMonths = findRowIndexByMeasure(
              getWOSMeasureKey("ending", viewParams.dateHierarchy).toLowerCase()
            );

            // All required rows must be found (review is now always present)
            const allRowsFound =
              idxStart != null &&
              idxArrivalsReview != null &&
              idxArrivalsApproved != null &&
              idxArrivalsOpen != null &&
              idxArrivalsTotal != null &&
              idxPlaced != null &&
              idxForecast != null &&
              idxEnding != null;

            if (allRowsFound) {
              const startBase = { ...getRowOriginal(idxStart) };
              const arrivalsReviewBase = {
                ...getRowOriginal(idxArrivalsReview),
              };
              const arrivalsApprovedBase = {
                ...getRowOriginal(idxArrivalsApproved),
              };
              const arrivalsOpenBase = { ...getRowOriginal(idxArrivalsOpen) };
              const forecast = getRowOriginal(idxForecast);

              // Get edited values
              const editedQty = parentEdit.quantity;
              const editedPlacedDate = parentEdit.orderPlacedDate;
              const editedDeliveryDate = parentEdit.deliveryDate;
              const isApproved = parentEdit.approved ?? false;

              // Check if we're in a revert state (parentEdit matches initialParentEdit)
              // Compare all editable fields to determine if we're reverted
              const isReverted =
                initialParentEdit &&
                parentEdit.quantity === initialParentEdit.quantity &&
                parentEdit.orderPlacedDate ===
                  initialParentEdit.orderPlacedDate &&
                parentEdit.deliveryDate === initialParentEdit.deliveryDate &&
                (parentEdit.total_cost ?? null) ===
                  (initialParentEdit.total_cost ?? null) &&
                (parentEdit.unit_cost ?? null) ===
                  (initialParentEdit.unit_cost ?? null);

              // Get original and committed values to handle both edit and revert cases
              let originalDeliveryDate: string | null = null;
              let originalQty: number | null = null;
              let committedDeliveryDate: string | null = null;
              let committedQty: number | null = null;

              if (poId && sku && location && supplyGuard?.getEdit) {
                const runAttemptId =
                  mainRun?.runSettings?.runAttemptId ||
                  lastSuccessfulRun?.runSettings?.runAttemptId ||
                  "no-run";
                const rowKey = `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`;
                const stored = supplyGuard.getEdit(rowKey);
                if (stored?.originalValues) {
                  originalDeliveryDate =
                    stored.originalValues.order_delivery_date ??
                    stored.originalValues["order_delivery_date"] ??
                    null;
                  const origQtyStr =
                    stored.originalValues.quantity ??
                    stored.originalValues["quantity"];
                  if (origQtyStr != null && origQtyStr !== "") {
                    originalQty = Number(String(origQtyStr).replace(/,/g, ""));
                  }
                }
                if (stored?.committedValues) {
                  committedDeliveryDate =
                    stored.committedValues.order_delivery_date ??
                    stored.committedValues["order_delivery_date"] ??
                    null;
                  const committedQtyStr =
                    stored.committedValues.quantity ??
                    stored.committedValues["quantity"];
                  if (committedQtyStr != null && committedQtyStr !== "") {
                    committedQty = Number(
                      String(committedQtyStr).replace(/,/g, "")
                    );
                  }
                }
              }

              // Fallback: if we don't have original values from storage, we need to derive them
              // The key insight: if a field wasn't edited, its original value = its current value
              // If a field was edited, its original value is what it was before the edit
              if (!originalDeliveryDate || originalQty === null) {
                // If deliveryDate wasn't in committed values, it wasn't edited, so original = current
                if (!originalDeliveryDate) {
                  // Check if deliveryDate was edited (is it in committed values?)
                  const deliveryDateWasEdited =
                    committedDeliveryDate != null &&
                    committedDeliveryDate !== "";
                  if (!deliveryDateWasEdited) {
                    // Delivery date wasn't edited, so original = current
                    originalDeliveryDate =
                      parentEdit.deliveryDate ||
                      initialParentEdit?.deliveryDate ||
                      null;
                  } else {
                    // Delivery date was edited, but we don't have original - this is a problem
                    // Try to get it from initialParentEdit as last resort
                    originalDeliveryDate =
                      initialParentEdit?.deliveryDate || null;
                  }
                }

                // If quantity wasn't in committed values, it wasn't edited, so original = current
                if (originalQty === null) {
                  const quantityWasEdited = committedQty != null;
                  if (!quantityWasEdited) {
                    // Quantity wasn't edited, so original = current
                    originalQty = parentEdit.quantity;
                  } else {
                    // Quantity was edited, but we don't have original - use initialParentEdit
                    if (initialParentEdit) {
                      originalQty = initialParentEdit.quantity;
                    }
                  }
                }
              }

              // Validate dates before using them - empty strings or invalid dates should use fallback
              const editedPlacedDateObj =
                editedPlacedDate && editedPlacedDate !== ""
                  ? dayjs.utc(editedPlacedDate)
                  : null;
              const editedDeliveryDateObj =
                editedDeliveryDate && editedDeliveryDate !== ""
                  ? dayjs.utc(editedDeliveryDate)
                  : null;

              // Check if dates are valid
              if (
                !editedPlacedDateObj ||
                !editedPlacedDateObj.isValid() ||
                !editedDeliveryDateObj ||
                !editedDeliveryDateObj.isValid()
              ) {
                // If dates are invalid, we can't recalculate properly
                // This shouldn't happen if merge logic works correctly
                // Return original row without recalculation if dates are missing/invalid
                return r;
              }

              const editedPlacedMonth = editedPlacedDateObj
                .startOf("month")
                .valueOf();
              const editedDeliveryMonth = editedDeliveryDateObj
                .startOf("month")
                .valueOf();

              // Calculate original delivery month if we have the original date
              let originalDeliveryMonth: number | null = null;
              if (originalDeliveryDate) {
                try {
                  const originalDateObj = dayjs.utc(originalDeliveryDate);
                  if (originalDateObj.isValid()) {
                    originalDeliveryMonth = originalDateObj
                      .startOf("month")
                      .valueOf();
                  }
                } catch (e) {
                  // Ignore parsing errors
                }
              }

              // Calculate committed delivery month (for revert case)
              let committedDeliveryMonth: number | null = null;
              if (committedDeliveryDate) {
                try {
                  const committedDateObj = dayjs.utc(committedDeliveryDate);
                  if (committedDateObj.isValid()) {
                    committedDeliveryMonth = committedDateObj
                      .startOf("month")
                      .valueOf();
                  }
                } catch (e) {
                  // Ignore errors for committed date
                }
              }

              // Build ordered month keys
              const endingRow = getRowOriginal(idxEnding);
              const endingDateValues = endingRow?.dateValues || {};
              const monthKeys = Object.keys(endingDateValues)
                .map((k) => Number(k))
                .sort((a, b) => a - b)
                .map((n) => String(n));

              // Compute PO placed: zero out all months except the edited PO month
              const computedPlaced: Record<string, number> = {};
              for (const ts of monthKeys) {
                computedPlaced[ts] =
                  ts === String(editedPlacedMonth) ? editedQty : 0;
              }

              // Compute arrivals: redistribute to approved or review based on edit status
              // IMPORTANT: Subtract old amount from old ETA date month when ETA date changes
              // IMPORTANT: Handle revert case to avoid doubling values
              const computedArrivalsReview: Record<string, number> = {};
              const computedArrivalsApproved: Record<string, number> = {};

              // If reverted, redistribute based on current approval status
              // The backend should have already reverted the values, but we still need to
              // redistribute between approved/review rows based on current approval status
              if (isReverted) {
                for (const ts of monthKeys) {
                  const baseReview =
                    Number(
                      String(arrivalsReviewBase?.dateValues?.[ts] ?? 0).replace(
                        /,/g,
                        ""
                      )
                    ) || 0;
                  const baseApproved =
                    Number(
                      String(
                        arrivalsApprovedBase?.dateValues?.[ts] ?? 0
                      ).replace(/,/g, "")
                    ) || 0;
                  // Sum existing values and redistribute based on current approval status
                  const existingTotal = baseReview + baseApproved;
                  computedArrivalsReview[ts] = isApproved ? 0 : existingTotal;
                  computedArrivalsApproved[ts] = isApproved ? existingTotal : 0;
                }
              } else {
                for (const ts of monthKeys) {
                  const baseReview =
                    Number(
                      String(arrivalsReviewBase?.dateValues?.[ts] ?? 0).replace(
                        /,/g,
                        ""
                      )
                    ) || 0;
                  const baseApproved =
                    Number(
                      String(
                        arrivalsApprovedBase?.dateValues?.[ts] ?? 0
                      ).replace(/,/g, "")
                    ) || 0;

                  // Sum existing values (one should be 0, the other has the value)
                  let existingTotal = baseReview + baseApproved;

                  // Handle revert case: subtract from committed month, add to original month
                  if (
                    isReverted &&
                    committedDeliveryMonth !== null &&
                    committedQty !== null
                  ) {
                    // When reverting, subtract from the committed (edited) delivery month
                    if (ts === String(committedDeliveryMonth)) {
                      existingTotal = Math.max(0, existingTotal - committedQty);
                    }
                    // Add back to original month (handled below in the editedDeliveryMonth check)
                  } else if (!isReverted) {
                    // Normal edit case: always subtract original quantity from original delivery month
                    // This handles both ETA date changes and quantity changes
                    // If we don't have original values, we can't subtract properly, so skip subtraction
                    // This will result in adding new quantity on top of old, which is incorrect
                    // but better than crashing. The original values should always be available from storage.
                    if (
                      originalDeliveryMonth !== null &&
                      originalQty !== null
                    ) {
                      if (ts === String(originalDeliveryMonth)) {
                        // Subtract the original quantity from the original delivery month
                        existingTotal = Math.max(
                          0,
                          existingTotal - originalQty
                        );
                      }
                    }
                  }

                  // Redistribute to correct row based on approval status
                  if (ts === String(editedDeliveryMonth)) {
                    // Add the new quantity to the new ETA date month
                    // If reverting, this adds back to the original month
                    // If editing, this adds to the new month (or replaces if same month)
                    // If quantity changed but month is same, we've already subtracted old qty above
                    const newTotal = existingTotal + editedQty;
                    computedArrivalsReview[ts] = isApproved ? 0 : newTotal;
                    computedArrivalsApproved[ts] = isApproved ? newTotal : 0;
                  } else {
                    // Keep other months as-is (with old amount subtracted if applicable)
                    computedArrivalsReview[ts] = isApproved ? 0 : existingTotal;
                    computedArrivalsApproved[ts] = isApproved
                      ? existingTotal
                      : 0;
                  }
                }
              }

              // Arrivals (open) remains unchanged
              const computedArrivalsOpen: Record<string, number> = {};
              for (const ts of monthKeys) {
                computedArrivalsOpen[ts] =
                  Number(
                    String(arrivalsOpenBase?.dateValues?.[ts] ?? 0).replace(
                      /,/g,
                      ""
                    )
                  ) || 0;
              }

              // Arrivals (total) = open + review + approved
              const computedArrivalsTotal: Record<string, number> = {};
              for (const ts of monthKeys) {
                computedArrivalsTotal[ts] =
                  computedArrivalsOpen[ts] +
                  computedArrivalsReview[ts] +
                  computedArrivalsApproved[ts];
              }

              // Cascade starts and endings
              const computedStart: Record<string, number> = {};
              const computedEnding: Record<string, number> = {};
              let prevEnding: number | null = null;
              for (let i = 0; i < monthKeys.length; i++) {
                const ts = monthKeys[i];
                const startVal: number =
                  prevEnding != null
                    ? prevEnding
                    : Number(
                        String(startBase?.dateValues?.[ts] ?? 0).replace(
                          /,/g,
                          ""
                        )
                      ) || 0;
                const f =
                  Number(
                    String(forecast?.dateValues?.[ts] ?? 0).replace(/,/g, "")
                  ) || 0;
                const a = computedArrivalsTotal[ts] || 0;
                const endVal: number = startVal + f + a;
                computedStart[ts] = startVal;
                computedEnding[ts] = endVal;
                prevEnding = endVal;
              }

              // Calculate unfulfilled demand and in stock rate
              const computedUnfulfilled: Record<string, number> = {};
              const computedInStockRate: Record<string, string> = {};
              for (let i = 0; i < monthKeys.length; i++) {
                const ts = monthKeys[i];
                const startVal = computedStart[ts];
                const f =
                  Number(
                    String(forecast?.dateValues?.[ts] ?? 0).replace(/,/g, "")
                  ) || 0;
                const a = computedArrivalsTotal[ts] || 0;
                const absFS = Math.abs(f);
                const unfulfilled = Math.max(0, absFS - startVal - a);
                const percent =
                  absFS === 0
                    ? 100
                    : Math.round(((absFS - unfulfilled) / absFS) * 100);
                computedUnfulfilled[ts] = unfulfilled;
                computedInStockRate[ts] = percent + "%";
              }

              // Calculate starting and ending months of supply
              const absForecastedSales = monthKeys.map((ts) => {
                const f =
                  Number(
                    String(forecast?.dateValues?.[ts] ?? 0).replace(/,/g, "")
                  ) || 0;
                return Math.abs(f);
              });
              const runningSums = [0];
              for (let i = 0; i < absForecastedSales.length; i++) {
                runningSums.push(runningSums[i] + absForecastedSales[i]);
              }

              const computedStartingMonths: Record<string, number> = {};
              for (let aIdx = 0; aIdx < monthKeys.length; aIdx++) {
                const ts = monthKeys[aIdx];
                const startingInventory = computedStart[ts];
                const absA = absForecastedSales[aIdx];
                let months = 0;
                for (let bIdx = aIdx; bIdx < monthKeys.length; bIdx++) {
                  const demand =
                    runningSums[bIdx + 1] - runningSums[aIdx + 1] + absA;
                  if (startingInventory >= demand) {
                    months++;
                  } else {
                    break;
                  }
                }
                computedStartingMonths[ts] = months;
              }

              const computedEndingMonths: Record<string, number> = {};
              for (let aIdx = 0; aIdx < monthKeys.length; aIdx++) {
                const ts = monthKeys[aIdx];
                const endingInventory = computedEnding[ts];
                let months = 0;
                for (let bIdx = aIdx + 1; bIdx < monthKeys.length; bIdx++) {
                  const demand = runningSums[bIdx + 1] - runningSums[aIdx + 1];
                  if (endingInventory >= demand) {
                    months++;
                  } else {
                    break;
                  }
                }
                computedEndingMonths[ts] = months;
              }

              const applyOverlay = (
                target: any,
                values: Record<string, number>
              ) => {
                const dv = { ...(target.dateValues || {}) } as Record<
                  string,
                  any
                >;
                Object.keys(values).forEach((k) => {
                  dv[k] = values[k];
                  if (/^\d{13}$/.test(k)) target[k] = values[k];
                });
                target.dateValues = dv;
              };

              const m = String(r.measure || "").toLowerCase();
              if (m === "starting inventory") {
                applyOverlay(r, computedStart);
              } else if (m === "purchase order to place") {
                applyOverlay(r, computedPlaced);
              } else if (m === "purchase order arrivals (review)") {
                applyOverlay(r, computedArrivalsReview);
              } else if (m === "purchase order arrivals (approved)") {
                applyOverlay(r, computedArrivalsApproved);
              } else if (m === "purchase order arrivals (open)") {
                applyOverlay(r, computedArrivalsOpen);
              } else if (m === "purchase order arrivals (total)") {
                applyOverlay(r, computedArrivalsTotal);
              } else if (m === "ending inventory") {
                applyOverlay(r, computedEnding);
              } else if (m === "unfulfilled demand" && idxUnfulfilled != null) {
                applyOverlay(r, computedUnfulfilled);
              } else if (m === "in stock rate" && idxInStockRate != null) {
                const dv = { ...(r.dateValues || {}) } as Record<string, any>;
                Object.keys(computedInStockRate).forEach((k) => {
                  dv[k] = computedInStockRate[k];
                  if (/^\d{13}$/.test(k)) r[k] = computedInStockRate[k];
                });
                r.dateValues = dv;
              } else if (
                idxStartingMonths != null &&
                m ===
                  getWOSMeasureKey(
                    "starting",
                    viewParams.dateHierarchy
                  ).toLowerCase()
              ) {
                applyOverlay(r, computedStartingMonths);
              } else if (
                idxEndingMonths != null &&
                m ===
                  getWOSMeasureKey(
                    "ending",
                    viewParams.dateHierarchy
                  ).toLowerCase()
              ) {
                applyOverlay(r, computedEndingMonths);
              }
            }
          }
        } catch (err) {
          console.error("[NestedDetails] Recalculation error:", err);
        }

        return r;
      };

      return { ...base, getRow: wrappedGetRow };
    }

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.parsed, parentEdit, viewParams, theme]);

  if (!entry || entry.loading)
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
          minHeight: 460,
        }}
      >
        Loading…
      </Box>
    );
  if (entry.error)
    return (
      <Box
        sx={{ p: 1, color: "error.main", width: "100%", textAlign: "center" }}
      >
        {entry.error}
      </Box>
    );

  if (!child)
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1,
        }}
      >
        No data (missing required fields for Supply Walk table)
      </Box>
    );

  const tableKey = `table-${
    entry.parsed && !isChunkedParsedData(entry.parsed)
      ? entry.parsed.arrowTable.numRows
      : 0
  }`;

  return (
    <FlatTable
      key={tableKey}
      columns={child.columns}
      getRow={child.getRow}
      rowCount={child.rowCount}
      showHeader={true}
      size="small"
      elevation={1}
      minWidth={400}
    />
  );
};
